import { useLocation, matchPath } from "react-router-dom";
import { SEO } from "./SEO";

/**
 * Centralized SEO for routes that don't render their own <SEO /> tags.
 * Pages that include their own <SEO> (Index, Browse, ItemDetail, FAQ, About,
 * Fees, Safety, HelpCenter, Contact, Privacy, Terms, PublicProfile) override
 * these values via react-helmet-async dedup by name/property.
 */
type Entry = { title: string; description: string };

const ROUTE_META: Record<string, Entry> = {
  "/auth": {
    title: "Sign in or sign up — Rentify",
    description: "Log in to your Rentify account or create a new one to rent items or list your own gear in your neighborhood.",
  },
  "/reset-password": {
    title: "Reset your password — Rentify",
    description: "Set a new password for your Rentify account so you can get back to renting and listing items.",
  },
  "/dashboard": {
    title: "Your dashboard — Rentify",
    description: "Manage your Rentify activity: track bookings, listings, earnings, and account status in one place.",
  },
  "/earnings": {
    title: "Earnings & payouts — Rentify",
    description: "Review your Rentify earnings, completed rentals, and upcoming payouts from items you list.",
  },
  "/edit-profile": {
    title: "Edit your profile — Rentify",
    description: "Update your Rentify profile details: name, city, avatar, phone, and identity verification.",
  },
  "/list-item": {
    title: "List an item for rent — Rentify",
    description: "Earn money renting out what you already own. List tools, cameras, gear, and more on Rentify in minutes.",
  },
  "/bookings": {
    title: "Your bookings — Rentify",
    description: "View pending, approved, and completed Rentify bookings, manage requests, and coordinate pickup with owners.",
  },
  "/support": {
    title: "Support — Rentify",
    description: "Get help with your Rentify rentals, listings, payments, or account from our support team.",
  },
  "/cart": {
    title: "Your cart — Rentify",
    description: "Review the items in your Rentify cart, adjust rental dates, and continue to secure checkout.",
  },
  "/checkout": {
    title: "Checkout — Rentify",
    description: "Confirm rental dates, review fees, and securely pay for your Rentify booking.",
  },
  "/payment-success": {
    title: "Payment confirmed — Rentify",
    description: "Your Rentify payment was successful. Coordinate pickup with the item owner and get ready for your rental.",
  },
  "/wishlist": {
    title: "Your wishlist — Rentify",
    description: "Items you've saved on Rentify to rent later, with quick access to listings and availability.",
  },
  "/messages": {
    title: "Messages — Rentify",
    description: "Chat with renters and owners on Rentify to coordinate pickup, return, and answer questions about rentals.",
  },
  "/saved-searches": {
    title: "Saved searches — Rentify",
    description: "Get notified when new Rentify items match your saved search filters for category, price, and location.",
  },
  "/rental-history": {
    title: "Rental history — Rentify",
    description: "Browse your full Rentify rental history with receipts, dates, and links to leave reviews.",
  },
  "/my-listings": {
    title: "Your listings — Rentify",
    description: "Manage the items you rent out on Rentify: edit details, pause listings, and track booking requests.",
  },
  "/my-reviews": {
    title: "Your reviews — Rentify",
    description: "See reviews you've written and received on Rentify across rentals and listed items.",
  },
  "/notifications": {
    title: "Notifications — Rentify",
    description: "Latest activity on your Rentify account: booking requests, messages, payouts, and reminders.",
  },
  "/rental-pending": {
    title: "Rental pending — Rentify",
    description: "Your Rentify booking is pending owner approval. We'll notify you the moment it's confirmed.",
  },
  "/settings": {
    title: "Account settings — Rentify",
    description: "Manage your Rentify account preferences, notifications, security, and connected payment accounts.",
  },
};

const DYNAMIC_PATTERNS: { pattern: string; entry: Entry }[] = [
  {
    pattern: "/chat/:bookingId",
    entry: {
      title: "Booking chat — Rentify",
      description: "Coordinate pickup, return, and questions for your Rentify booking with the other party in real time.",
    },
  },
  {
    pattern: "/receipt/:bookingId",
    entry: {
      title: "Booking receipt — Rentify",
      description: "View and download the receipt for your Rentify rental, including fees, dates, and payout details.",
    },
  },
  {
    pattern: "/rental/:bookingId",
    entry: {
      title: "Rental details — Rentify",
      description: "Full details for your Rentify rental: dates, pickup and return verification codes, and contact info.",
    },
  },
  {
    pattern: "/rental-thank-you/:bookingId",
    entry: {
      title: "Thanks for renting — Rentify",
      description: "Your Rentify rental is wrapped up. Share a review and help build trust in the community.",
    },
  },
];

export const RouteSEO = () => {
  const { pathname } = useLocation();
  let entry: Entry | undefined = ROUTE_META[pathname];
  if (!entry) {
    for (const { pattern, entry: e } of DYNAMIC_PATTERNS) {
      if (matchPath(pattern, pathname)) {
        entry = e;
        break;
      }
    }
  }
  if (!entry) return null;
  return <SEO title={entry.title} description={entry.description} path={pathname} />;
};

export default RouteSEO;
